import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PlanItemResponse } from "../../openapi/client";
import { useMediaQuery } from "@react-hook/media-query";

interface MealCardProps {
  meal?: PlanItemResponse;
  title?: string;
  subtitle?: string;
  mealTypes?: string;
  features?: string[];
  price?: string;
  partner?: string;
  imageUrl?: string;
}

/**
 * Shared hook to process meal card data for both mobile and desktop components
 * Unifies the logic for extracting card properties from API data or props
 */
export const useMealCardData = (props: MealCardProps) => {
  const { t } = useTranslation("mealCard");
  const mobile = useMediaQuery("only screen and (max-width: 600px)");
  const navigate = useNavigate();
  const { meal, title, subtitle, mealTypes, features = [], price, partner, imageUrl } = props;

  // Use API data if available, otherwise use props
  const cardTitle = meal?.name || title;

  // cardSubtitle - shows the plan summary
  const cardSubtitle = meal?.summary || subtitle;

  // cardMealTypes - not available in new API, use props fallback
  const cardMealTypes = mealTypes;

  const cardPrice = meal?.minDailyPrice?.toFixed(2) || price;
  const cardPartner = meal?.partner?.name || partner;
  const cardImageUrl = imageUrl; // Image URL is prepared by parent component

  // Build features list from API data or use provided features (matching AllPlans/PlanPreferences pattern)
  const cardFeatures = meal
    ? [
        t("minDeliveries", { count: meal.minDeliveries }),
        ...(meal.ingredientEditable ? [t("personalizedIngredients")] : []),
        t("skipAnytime"),
      ]
    : features;

  // Navigation handler - navigates to meal-selection page (matching PlanPreferences pattern)
  const buyHandler = () => {
    if (!meal) return;
    navigate(`/partners/${meal.partner?.slug}/${meal.slug}/meal-selection`);
  };

  return {
    cardTitle,
    cardSubtitle,
    cardMealTypes,
    cardPrice,
    cardPartner,
    cardImageUrl,
    cardFeatures,
    buyHandler,
    t,
  };
};
